import { useRef } from "react";

const AREAS = [
  {
    icon: "ri-heart-pulse-line",
    title: "Pensão por Morte",
    subtitle: "Para dependentes que perderam um ente querido",
    description:
      "Se você perdeu um cônjuge, companheiro(a) ou pai/mãe que era segurado(a) do INSS, você pode ter direito à pensão por morte. Cuidamos de todo o processo para garantir que você receba esse benefício com segurança e agilidade.",
    items: [
      "Cônjuge e companheiro(a)",
      "Filhos menores de 21 anos",
      "Dependentes econômicos",
      "Processos administrativos e judiciais",
    ],
  },
  {
    icon: "ri-scales-3-line",
    title: "Cumulação de Pensão",
    subtitle: "Receba mais de um benefício ao mesmo tempo",
    description:
      "Em alguns casos, é possível receber duas pensões por morte simultaneamente, ou combinar a pensão com outro benefício previdenciário. Analisamos sua situação para maximizar o que você tem direito.",
    items: [
      "Duas pensões por morte",
      "Pensão + aposentadoria",
      "Análise de elegibilidade",
      "Planejamento previdenciário",
    ],
  },
  {
    icon: "ri-shield-user-line",
    title: "Benefício LOAS",
    subtitle: "Amparo Social para quem mais precisa",
    description:
      "O BPC/LOAS é um salário mínimo mensal destinado a idosos acima de 65 anos e pessoas com deficiência em situação de vulnerabilidade. Se você foi negado, podemos reverter isso na Justiça.",
    items: [
      "Idosos acima de 65 anos",
      "Pessoas com deficiência",
      "Recursos de negativas do INSS",
      "Análise de renda familiar",
    ],
  },
];

function AreaCard({ area }: { area: (typeof AREAS)[0] }) {
  return (
    <article
      className="flex flex-col rounded-2xl overflow-hidden border transition-shadow duration-300 hover:shadow-md h-full"
      style={{ backgroundColor: "#FAF7F3", borderColor: "#EBE3D8" }}
    >
      <div className="p-8 flex flex-col gap-6 flex-1">
        {/* Icon + Title */}
        <div className="flex flex-col gap-4">
          <div
            className="w-10 h-10 flex items-center justify-center rounded-xl"
            style={{ backgroundColor: "rgba(61, 35, 32, 0.07)" }}
          >
            <i
              className={`${area.icon} text-lg`}
              style={{ color: "rgba(61, 35, 32, 0.5)" }}
            />
          </div>
          <div>
            <h3
              className="font-display text-xl font-semibold leading-tight"
              style={{ color: "rgb(61, 35, 32)" }}
            >
              {area.title}
            </h3>
            <p
              className="font-body text-xs font-medium mt-1.5 tracking-wide"
              style={{ color: "rgba(61, 35, 32, 0.45)" }}
            >
              {area.subtitle}
            </p>
          </div>
        </div>

        <div className="w-full h-px" style={{ backgroundColor: "#EBE3D8" }} />

        <p
          className="font-body text-sm leading-relaxed"
          style={{ color: "rgba(61, 35, 32, 0.6)" }}
        >
          {area.description}
        </p>

        <ul className="flex flex-col gap-2.5 flex-1">
          {area.items.map((item) => (
            <li key={item} className="flex items-start gap-3">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i
                  className="ri-checkbox-circle-line text-sm"
                  style={{ color: "rgba(61, 35, 32, 0.4)" }}
                />
              </div>
              <span
                className="font-body text-sm"
                style={{ color: "rgba(61, 35, 32, 0.65)" }}
              >
                {item}
              </span>
            </li>
          ))}
        </ul>

        <a
          href="https://wa.me/5581995913182?text=Ol%C3%A1%2C%20tenho%20uma%20d%C3%BAvida%20sobre%20meu%20benef%C3%ADcio"
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="mt-2 inline-flex items-center gap-2 font-body text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 group"
          style={{ color: "rgb(61, 35, 32)" }}
        >
          Consultar agora
          <div
            className="w-5 h-5 flex items-center justify-center rounded-full transition-all duration-200 group-hover:translate-x-1"
            style={{ backgroundColor: "rgba(61, 35, 32, 0.1)" }}
          >
            <i className="ri-arrow-right-line text-xs" />
          </div>
        </a>
      </div>
    </article>
  );
}

export default function PracticeAreas() {
  const scrollRef = useRef<HTMLDivElement>(null);

  return (
    <section id="areas" className="w-full py-28 px-6" style={{ backgroundColor: "#FDFAF7" }}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="font-body text-peach text-xs tracking-[0.35em] uppercase font-medium mb-4">
            O que fazemos
          </p>
          <h2
            className="font-display text-4xl lg:text-5xl font-semibold leading-tight mb-4"
            style={{ color: "rgb(61, 35, 32)" }}
          >
            Áreas de{" "}
            <span className="font-light italic" style={{ color: "rgb(61, 35, 32)" }}>
              Atuação
            </span>
          </h2>
          <div className="w-10 h-px bg-peach mx-auto mt-6 mb-7" />
          <p
            className="font-body text-base max-w-xl mx-auto leading-relaxed"
            style={{ color: "rgba(61, 35, 32, 0.55)" }}
          >
            Atuamos com foco total no Direito Previdenciário, garantindo que você
            receba os benefícios que são seus por direito — com clareza,
            comprometimento e resultados.
          </p>
        </div>

        {/* ── MOBILE: horizontal carousel, centered ── */}
        <div className="md:hidden">
          <div
            ref={scrollRef}
            className="flex overflow-x-auto gap-5 pb-4 snap-x snap-mandatory no-scrollbar"
            style={{
              paddingLeft: "calc(50% - 41vw)",
              paddingRight: "calc(50% - 41vw)",
            }}
          >
            {AREAS.map((area) => (
              <div
                key={area.title}
                className="snap-center flex-shrink-0"
                style={{ width: "82vw" }}
              >
                <AreaCard area={area} />
              </div>
            ))}
          </div>

          {/* Dots indicator */}
          <div className="flex justify-center gap-2 mt-5">
            {AREAS.map((area, idx) => (
              <button
                key={idx}
                onClick={() => {
                  if (scrollRef.current) {
                    const containerWidth = scrollRef.current.offsetWidth;
                    const cardWidth = containerWidth * 0.82;
                    const gap = 20;
                    const paddingLeft = containerWidth * 0.5 - containerWidth * 0.41;
                    const offset = paddingLeft + idx * (cardWidth + gap);
                    scrollRef.current.scrollTo({ left: offset, behavior: "smooth" });
                  }
                }}
                className="w-2 h-2 rounded-full transition-colors duration-200 cursor-pointer"
                style={{ backgroundColor: "rgba(61, 35, 32, 0.25)" }}
                aria-label={`Ir para ${area.title}`}
              />
            ))}
          </div>
        </div>

        {/* ── DESKTOP: grid ── */}
        <div className="hidden md:grid grid-cols-3 gap-6 mt-12">
          {AREAS.map((area) => (
            <AreaCard key={area.title} area={area} />
          ))}
        </div>
      </div>
    </section>
  );
}
